'use client';

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { ShoppingBag } from 'lucide-react';
import { Fragment, useRef } from 'react';
import { SignedIn } from '@clerk/nextjs';
import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';

import { getValidImageAltText, getValidImageUrl } from '@lib/utils/image';
import { RemoveCartProduct } from '@components/common/remove-cart-product';
import { useCartProducts } from '@lib/cart/hooks/useCartProducts';
import { Separator } from '@components/common/separator';
import { Button } from '@components/common/button';
import { Routes } from '@lib/routes';
import { slugify } from '@lib/utils';

export function LowerHeaderShoppingCart() {
  const { cartProducts, isLoading } = useCartProducts();
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <SignedIn>
      <Popover className='text-sm relative'>
        {({ close, open }) => (
          <>
            <PopoverButton
              ref={btnRef}
              className={clsx(
                '-m-2 flex items-center p-2 outline-none hover:text-primary-foreground relative',
                open ? 'text-primary-foreground' : 'text-primary-foreground/80',
              )}
            >
              <ShoppingBag
                className={clsx(
                  'size-[22px] flex-shrink-0',
                  isLoading && 'animate-pulse',
                )}
                aria-hidden='true'
              />

              <span
                className={clsx(
                  'text-sm font-medium relative opacity-0 transition-all duration-200 ease-in-out',
                  isLoading ? '-ml-2' : 'ml-2 opacity-100',
                )}
              >
                {cartProducts.length}
              </span>

              <span className='sr-only'> products in cart, view bag</span>
            </PopoverButton>

            <Transition
              as={Fragment}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <PopoverPanel className='absolute right-0 lg:w-80 mt-3 min-w-40 rounded-md bg-white shadow-2xl z-10 overflow-hidden ring-1 ring-black ring-opacity-5 focus:outline-none'>
                <h2 className='sr-only'>Shopping Cart</h2>

                <div className='max-w-2xl'>
                  {cartProducts.length === 0 ? (
                    <div className='w-full flex-center my-6'>
                      <span className='text-center text-gray-500'>
                        Your cart is empty.
                      </span>
                    </div>
                  ) : (
                    <ul
                      role='list'
                      className='divide-y divide-gray-200 max-h-[256px] lg:max-h-[512px] overflow-y-auto px-4 pb-6 space-y-6'
                    >
                      {cartProducts.map((product) => (
                        <li
                          key={product.id}
                          className='flex-between !items-start gap-x-4 pt-6'
                        >
                          <Link
                            href={`${Routes.product}/${slugify(product.name)}/details/${product.id}`}
                            className='flex items-start gap-x-4'
                          >
                            <Image
                              src={getValidImageUrl(product.images)}
                              alt={getValidImageAltText(
                                product.images,
                                `${product.name}'s Image`,
                              )}
                              height={64}
                              width={64}
                              className='flex-none rounded-md aspect-square'
                            />
                            <div className='flex-auto'>
                              <h3 className='font-medium leading-tight line-clamp-2 text-ellipsis'>
                                {product.name}
                              </h3>
                              <span className='text-gray-600'>
                                ${product.totalPrice.toFixed(2)}
                              </span>
                            </div>
                          </Link>
                          <RemoveCartProduct product={product} />
                        </li>
                      ))}
                    </ul>
                  )}

                  {cartProducts.length > 0 && (
                    <Separator aria-hidden='true' className='mb-6 lg:mb-8' />
                  )}

                  <div className='flex-center flex-col gap-y-2 px-5 pb-4 w-full'>
                    {cartProducts.length > 0 && (
                      <Button asChild onClick={() => btnRef.current?.click()}>
                        <Link href={Routes.cart}>View Shopping Bag</Link>
                      </Button>
                    )}

                    <Button
                      variant={cartProducts.length > 0 ? 'link' : 'default'}
                      onClick={close}
                      className={clsx(cartProducts.length > 0 && 'w-fit')}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    </SignedIn>
  );
}
